import { useEffect, useRef } from 'react';
import { CHAT_GPT_ROLES } from '../constants/chatRoles.constants';
import { CHAT_MEDIA_RETURN_TYPE } from '../constants/chatRequests.constants';

const isPersonMessage = (message) => {
	if (!message) {
		return false;
	}

	if (
		message.role === CHAT_GPT_ROLES.USER
		|| message.role === CHAT_GPT_ROLES.SYSTEM
	) {
		return false;
	}

	if (message.role === CHAT_GPT_ROLES.ASSISTANT) {
		return true;
	}

	return (
		message.response_format === CHAT_MEDIA_RETURN_TYPE.IMAGE
		|| message.response_format === CHAT_MEDIA_RETURN_TYPE.VIDEO
		|| Boolean(message.video)
	);
};

export const useComposerAutoFocus = ({
	inputRef,
	conversationId,
	messages,
	isVoiceCallActive,
	isComposerLocked,
	isLoadingConversation,
	isRecording,
	isTranscribing,
	isGalleryOpen,
}) => {
	const previousMessagesLengthRef = useRef(0);
	const hasInitialFocusRef = useRef(false);

	const shouldSkipFocus = () =>
		isVoiceCallActive
		|| isComposerLocked
		|| isRecording
		|| isTranscribing
		|| isGalleryOpen;

	const focusComposer = () => {
		window.requestAnimationFrame(() => {
			inputRef.current?.focus({ preventScroll: true });
		});
	};

	useEffect(() => {
		previousMessagesLengthRef.current = 0;
		hasInitialFocusRef.current = false;
	}, [conversationId]);

	useEffect(() => {
		if (isLoadingConversation) {
			return;
		}

		if (!hasInitialFocusRef.current) {
			if (shouldSkipFocus()) {
				return;
			}

			hasInitialFocusRef.current = true;
			previousMessagesLengthRef.current = messages.length;
			focusComposer();
			return;
		}

		if (shouldSkipFocus()) {
			return;
		}

		if (messages.length <= previousMessagesLengthRef.current) {
			previousMessagesLengthRef.current = messages.length;
			return;
		}

		previousMessagesLengthRef.current = messages.length;
		const lastMessage = messages[messages.length - 1];

		if (!isPersonMessage(lastMessage)) {
			return;
		}

		focusComposer();
	}, [
		conversationId,
		inputRef,
		isComposerLocked,
		isGalleryOpen,
		isLoadingConversation,
		isRecording,
		isTranscribing,
		isVoiceCallActive,
		messages,
	]);
};
